import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "features/auth";
import type { User } from "features/auth";
import { api, ApiError } from "utils/api";
import type { ProfileResp, RewardBalance } from "../types";

// NOTE: use Auth context for user info for now...
// const getProfile = () => api("/profile");
// const useProfile = () => useQuery<User, ApiError>(["profile"], getProfile);

const getProfilePoints = (userId: string): Promise<RewardBalance> =>
  api(`/users/${userId}/points`);

// Use the specified user or the signed in user
const useProfilePoints = (profileUser?: User) => {
  const { user: signedIn } = useAuth();
  const user = profileUser || signedIn;
  const cacheKey = profileUser
    ? ["admin-user-points", profileUser.id]
    : ["profile", "balance"];

  return useQuery<RewardBalance, ApiError>(
    cacheKey,
    () => getProfilePoints(user?.id as string),
    {
      enabled: !!user,
    }
  );
};

const updateProfile = (updates: Partial<User>): Promise<ProfileResp> =>
  api("/profile", {
    method: "PUT",
    body: JSON.stringify(updates),
  });

const useUpdateProfile = () => {
  const { refreshAuth } = useAuth();
  const queryClient = useQueryClient();

  const { mutate, ...mutation } = useMutation(updateProfile, {
    onSuccess: (resp) => {
      queryClient.invalidateQueries("profile");
      // NOTE: User data is embedded within their authentication token, so updates to their info
      // will invalidate their current token resulting in 403 errors on further calls. This will
      // refresh
      refreshAuth(resp);
    },
  });

  return {
    updateProfile: mutate,
    ...mutation,
  };
};

const resetStagingProfileBalance = (): Promise<ProfileResp> =>
  api("/profile/stagingPointsReset");

const useResetStagingProfileBalance = () => {
  const { refreshAuth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(resetStagingProfileBalance, {
    onSuccess: (resp) => {
      queryClient.invalidateQueries("profile");
      // NOTE: User data is embedded within their authentication token, so updates to their info
      // will invalidate their current token resulting in 403 errors on further calls. This will
      // refresh
      refreshAuth(resp);
    },
  });
};

const stagingAirDropPoints = (points: number): Promise<ProfileResp> =>
  api(`/profile/stagingPointsGrant?points=${points}`);

const useStagingAirDropPoints = () => {
  const { refreshAuth } = useAuth();
  const queryClient = useQueryClient();

  const { mutate, ...mutation } = useMutation(stagingAirDropPoints, {
    onSuccess: (resp) => {
      queryClient.invalidateQueries("profile");
      // NOTE: User data is embedded within their authentication token, so updates to their info
      // will invalidate their current token resulting in 403 errors on further calls. This will
      // refresh
      refreshAuth(resp);
    },
  });
  return { airDrop: mutate, ...mutation };
};

export {
  useProfilePoints,
  useUpdateProfile,
  useResetStagingProfileBalance,
  useStagingAirDropPoints,
};
